<script setup>
const { locale, t } = useI18n();
const route = useRoute();

import { pageTransition } from '~/helpers/animations/animations';

definePageMeta({
  pageTransition: pageTransition,
});

const fundSearch = ref('');
const filterNames = ref('');
const filterAuthors = ref('');
const filterArticles = ref('');
const filterYears = ref('');
const openFunds = ref([]);
const lastSelectedFilter = ref(null);

const pageQuery = gql`
  query GetPageData($routeId: String!) {
    page(route_id: $routeId) {
      breadcrumbs {
        id
        name
        route_id
        slug: seo_url_slug {
          cs
          en
        }
      }
      seo_meta_title
      seo_meta_description
      seo_meta_keywords
      name
      content
    }
    documents(types: ["metodic-center"]) {
      name
      file {
        url
      }
      books_author_name
      books_name
      books_publisher
      books_published_year
      description
      id
      periodicals_name
      periodicals_article_author
      periodicals_article_name
      periodicals_published_year
      section {
        name
      }
    }
  }
`;

const { data } = await useAsyncQuery(pageQuery, {
  routeId: route.name,
});

const page = ref(data.value?.page);
const funds = ref(data.value?.documents);

useHeadData({
  title: page.value?.seo_meta_title || page.value?.name || null,
  description: page.value?.seo_meta_description || null,
  keywords: page.value?.seo_meta_keywords || null,
  image: page.value?.image || page.value?.cropped_detail_image || null,
  url: route.fullPath,
});

const fundCategories = computed(() => {
  let fundCategories = [];

  funds.value.forEach((fund) => {
    fundCategories.push(fund.section.name);
  });

  // Get all unique values in a JavaScript array (remove duplicates)
  fundCategories = fundCategories.filter((a, b, array) => array.indexOf(a) === b);

  // Sort alphabetically
  fundCategories = fundCategories.sort((a, b) => a.localeCompare(b));

  return fundCategories;
});

const breadcrumbs = computed(() => {
  return page.value && page.value?.breadcrumbs
    ? [...page.value?.breadcrumbs].map((x, key) => ({
        title: x.name,
        route: key + 1 == page.value?.breadcrumbs.length ? null : checkAboutPageRoute(x),
      }))
    : [];
});

const checkAboutPageRoute = (path) => {
  if (path.slug[locale] == route.path) {
    return null;
  } else if (path.route_id == null) {
    return useGetRoute(path, page.value?.breadcrumbs[0].route_id);
  } else {
    return useGetRoute(path).name;
  }
};

const filteredFunds = computed(() => {
  return funds.value.filter((item) => {
    if (filterSearch(item.description) && fundSearch.value != '') {
      addFund(item.id);
    }

    if (
      item.section.name == 'Periodika' &&
      filterByName(item.periodicals_name) &&
      filterByAuthor(item.periodicals_article_author) &&
      filterByArticle(item.periodicals_article_name) &&
      filterByYear(item.books_published_year || item.periodicals_published_year)
    ) {
      return (
        filterSearch(item.books_author_name) ||
        filterSearch(item.periodicals_name) ||
        filterSearch(item.periodicals_article_author) ||
        filterSearch(item.books_publisher) ||
        filterSearch(item.periodicals_article_name) ||
        filterSearch(item.books_published_year) ||
        filterSearch(item.periodicals_published_year) ||
        filterSearch(item.description)
      );
    }
  });
});

const searchUnite = (text) => {
  return text
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase();
};

const filterSearch = (name) => {
  return name ? searchUnite(name).indexOf(searchUnite(fundSearch.value)) > -1 : null;
};

const names = computed(() => {
  let names = [];
  let from = filteredFunds.value;

  if (lastSelectedFilter.value == 'names') {
    from = funds.value.filter((item) => {
      if (
        item.section.name == 'Periodika' &&
        filterByAuthor(item.periodicals_article_author) &&
        filterByArticle(item.periodicals_article_name) &&
        filterByYear(item.books_published_year || item.periodicals_published_year)
      ) {
        return (
          filterSearch(item.books_author_name) ||
          filterSearch(item.periodicals_name) ||
          filterSearch(item.periodicals_article_author) ||
          filterSearch(item.books_publisher) ||
          filterSearch(item.periodicals_article_name) ||
          filterSearch(item.books_published_year) ||
          filterSearch(item.periodicals_published_year) ||
          filterSearch(item.description)
        );
      }
    });
  }

  from.forEach((fund) => {
    if (fund.periodicals_name && fund.section.name == 'Periodika' && names.indexOf(fund.periodicals_name) === -1) {
      names.push(fund.periodicals_name);
    }
  });

  // Sort alphabetically
  names = names.sort((a, b) => a.localeCompare(b));

  return names;
});

const authors = computed(() => {
  let authors = [];
  let from = filteredFunds.value;

  if (lastSelectedFilter.value == 'authors') {
    from = funds.value.filter((item) => {
      if (
        item.section.name == 'Periodika' &&
        filterByName(item.periodicals_name) &&
        filterByArticle(item.periodicals_article_name) &&
        filterByYear(item.books_published_year || item.periodicals_published_year)
      ) {
        return (
          filterSearch(item.books_author_name) ||
          filterSearch(item.periodicals_name) ||
          filterSearch(item.periodicals_article_author) ||
          filterSearch(item.books_publisher) ||
          filterSearch(item.periodicals_article_name) ||
          filterSearch(item.books_published_year) ||
          filterSearch(item.periodicals_published_year) ||
          filterSearch(item.description)
        );
      }
    });
  }

  from.forEach((fund) => {
    if (fund.section.name == 'Periodika') {
      if (fund.periodicals_article_author) {
        fund.periodicals_article_author
          .replace(/'; '/g, ';')
          .split(';')
          .forEach((author) => {
            if (author && authors.indexOf(author.trim()) === -1) {
              authors.push(author.trim());
            }
          });
      }
    }
  });

  // Sort alphabetically
  authors = authors.sort((a, b) => a.localeCompare(b));

  return authors;
});

const articles = computed(() => {
  let articles = [];
  let from = filteredFunds.value;

  if (lastSelectedFilter.value == 'articles') {
    from = funds.value.filter((item) => {
      if (
        item.section.name == 'Periodika' &&
        filterByName(item.periodicals_name) &&
        filterByAuthor(item.periodicals_article_author) &&
        filterByYear(item.books_published_year || item.periodicals_published_year)
      ) {
        return (
          filterSearch(item.books_author_name) ||
          filterSearch(item.periodicals_name) ||
          filterSearch(item.periodicals_article_author) ||
          filterSearch(item.books_publisher) ||
          filterSearch(item.periodicals_article_name) ||
          filterSearch(item.books_published_year) ||
          filterSearch(item.periodicals_published_year) ||
          filterSearch(item.description)
        );
      }
    });
  }

  from.forEach((fund) => {
    if (fund.periodicals_article_name && fund.section.name == 'Periodika' && articles.indexOf(fund.periodicals_article_name) === -1) {
      articles.push(fund.periodicals_article_name);
    }
  });

  // Sort alphabetically
  articles = articles.sort((a, b) => a.localeCompare(b));

  return articles;
});

const years = computed(() => {
  let years = [];
  let from = filteredFunds.value;

  if (lastSelectedFilter.value == 'years') {
    from = funds.value.filter((item) => {
      if (filterByArticle(item.books_publisher) && filterByName(item.name) && filterByAuthor(item.books_author_name || item.periodicals_name)) {
        return (
          filterSearch(item.name) ||
          filterSearch(item.books_author_name) ||
          filterSearch(item.periodicals_name) ||
          filterSearch(item.periodicals_article_author) ||
          filterSearch(item.books_publisher) ||
          filterSearch(item.periodicals_article_name) ||
          filterSearch(item.books_published_year) ||
          filterSearch(item.periodicals_published_year) ||
          filterSearch(item.description)
        );
      }
    });
  }

  from.forEach((fund) => {
    if (fund.section.name == 'Periodika') {
      if (fund.periodicals_published_year && years.indexOf(fund.periodicals_published_year) === -1) {
        years.push(fund.periodicals_published_year);
      }
      if (fund.books_published_year && years.indexOf(fund.books_published_year) === -1) {
        years.push(fund.books_published_year);
      }
    }
  });

  // Sort alphabetically
  years = years.sort((a, b) => a.localeCompare(b));

  return years;
});

const setUsedFilter = (filter) => {
  lastSelectedFilter.value = filter;
};

const filterByName = (name) => {
  if (filterNames.value) {
    return filterNames.value == name ? true : false;
  }

  return true;
};

const filterByAuthor = (author) => {
  if (author) {
    if (filterAuthors.value) {
      return author.includes(filterAuthors.value) ? true : false;
    }
  } else {
    return filterAuthors.value ? false : true;
  }

  return true;
};

const filterByArticle = (article) => {
  if (filterArticles.value) {
    return filterArticles.value == article ? true : false;
  }

  return true;
};

const filterByYear = (year) => {
  if (filterYears.value) {
    return filterYears.value == year ? true : false;
  }

  return true;
};

const clearFilter = () => {
  fundSearch.value = '';
  filterNames.value = '';
  filterAuthors.value = '';
  filterArticles.value = '';
  filterYears.value = '';
  openFunds.value = [];
};

const toggleFund = (id) => {
  if (openFunds.value.indexOf(id) === -1) {
    addFund(id);
  } else {
    removeFund(id);
  }
};
const addFund = (id) => {
  if (openFunds.value.indexOf(id) === -1) {
    openFunds.value.push(id);
  }
};
const removeFund = (id) => {
  openFunds.value = openFunds.value.filter((e) => e !== id);
};
const isInFunds = (id) => {
  return openFunds.value.includes(id);
};
</script>

<template>
  <main class="main">
    <!-- <Breadcrumbs :items="breadcrumbs" /> -->
    <h1 class="heading">{{ page.name }}</h1>
    <DynamicPageContent v-if="page.content" :content="page.content" class="mt-5" />
    <div class="mt-4">
      <label for="search" class="hidden">{{ t('pages.library.books.search') }}</label>
      <input id="search" type="text" class="input input--search" :placeholder="t('pages.library.books.search')" v-model="fundSearch" />
    </div>

    <div class="container-xl ml-0 pl-0">
      <div class="select-group mt-4">
        <div class="select-group__item">
          <div class="select-wrap">
            <select class="select select--wide select--box" name="filter-years" id="filter-names" v-model="filterNames" v-on:change="setUsedFilter('names')">
              <option disabled value="">{{ t('pages.library.name') }}</option>
              <option v-for="(item, key) in names" :key="key" :value="item">
                {{ item }}
              </option>
            </select>
          </div>
        </div>
        <div class="select-group__item">
          <div class="select-wrap">
            <select class="select select--wide select--box" name="filter-years" id="filter-authors" v-model="filterAuthors" v-on:change="setUsedFilter('authors')">
              <option disabled value="">{{ t('pages.library.books_author_name') }}</option>
              <option v-for="(item, key) in authors" :key="key" :value="item">
                {{ item }}
              </option>
            </select>
          </div>
        </div>
        <div class="select-group__item">
          <div class="select-wrap">
            <select class="select select--wide select--box" name="filter-years" id="filter-articles" v-model="filterArticles" v-on:change="setUsedFilter('articles')">
              <option disabled value="">{{ t('pages.library.periodicals_article_name') }}</option>
              <option v-for="(item, key) in articles" :key="key" :value="item">
                {{ item }}
              </option>
            </select>
          </div>
        </div>
        <div class="select-group__item">
          <div class="select-wrap">
            <select class="select select--wide select--box" name="filter-years" id="filter-years" v-model="filterYears" v-on:change="setUsedFilter('years')">
              <option disabled value="">{{ t('pages.library.books_published_year') }}</option>
              <option v-for="(item, key) in years" :key="key" :value="item">
                {{ item }}
              </option>
            </select>
          </div>
        </div>
      </div>
    </div>

    <div class="container-xl ml-0 pl-0 mt-4">
      <a @click.prevent="clearFilter" class="link clear-filter" v-if="fundSearch !== '' || filterNames || filterAuthors || filterArticles || filterYears">{{ t('global.clear-filter') }}</a>
    </div>

    <div v-for="(fundCategory, key) in fundCategories" :key="key">
      <template v-if="fundCategory == 'Periodika'">
        <h2 class="heading heading--2 heading--thin mt-5">{{ fundCategory }}</h2>

        <div class="container-xl ml-0 pl-0">
          <div class="table mt-4">
            <table class="table__elem">
              <thead class="table__head">
                <tr class="table__row">
                  <th class="table__header">
                    {{ t('pages.library.name') }}
                  </th>
                  <th class="table__header">
                    <template v-if="key == 0">
                      {{ t('pages.library.books_author_name') }}
                    </template>
                    <template v-else>
                      {{ t('pages.library.periodicals_name') }}
                    </template>
                  </th>
                  <th class="table__header" v-if="key != 0">
                    {{ t('pages.library.periodicals_article_author') }}
                  </th>
                  <th class="table__header">
                    <template v-if="key == 0">
                      {{ t('pages.library.books_publisher') }}
                    </template>
                    <template v-else>
                      {{ t('pages.library.periodicals_article_name') }}
                    </template>
                  </th>
                  <th class="table__header">
                    {{ t('pages.library.books_published_year') }}
                  </th>
                </tr>
              </thead>
              <tbody class="table__body">
                <template v-for="(fund, index) in filteredFunds" :key="index">
                  <tr v-if="fund.section.name == 'Periodika'" class="table__row">
                    <td class="table__cell" style="min-width: 200px">
                      <span v-if="fund.description" class="link text text--3 upper" v-on:click="toggleFund(fund.id)">{{ fund.books_author_name || fund.periodicals_name }}</span>
                      <span v-else class="text text--3 upper">{{ fund.books_author_name || fund.periodicals_name }}</span>
                    </td>
                    <td class="table__cell" v-if="!fund.books_name">
                      <span class="text text--3 upper">{{ fund.periodicals_article_author }}</span>
                    </td>
                    <td class="table__cell">
                      <span class="text text--3 upper">{{ fund.books_publisher || fund.periodicals_article_name }}</span>
                    </td>
                    <td class="table__cell">
                      <span class="text text--3">{{ fund.books_published_year || fund.periodicals_published_year }}</span>
                    </td>
                  </tr>
                  <tr v-if="fund.description" class="table__row" :class="{ 'is-hidden': !isInFunds(fund.id) }">
                    <td colspan="4" class="table__cell table__cell--extra">
                      <div class="table__toggle">
                        <div class="table__toggle-content">
                          <span class="text text--3">{{ fund.description }}</span>
                        </div>
                        <span class="table__toggle-close">
                          <i v-on:click="removeFund(fund.id)" class="icon-cross"></i>
                        </span>
                      </div>
                    </td>
                  </tr>
                </template>
              </tbody>
            </table>
          </div>
        </div>
      </template>
    </div>
  </main>
</template>

<style lang="scss" scoped>
.select-group {
  display: flex;
  flex-wrap: wrap;
  margin-right: -6px;
  margin-bottom: -6px;

  @include mq($md, true) {
    display: block;

    .select {
      width: 100% !important;
    }
  }

  &__item {
    display: inline-block;
    margin-right: 6px;
    margin-bottom: 6px;
    flex-shrink: 1;

    @include mq($md, true) {
      display: block;
    }
  }

  .select-wrap {
    width: 100%;
  }
}

.clear-filter {
  display: inline-block;
}

#filter-names {
  width: 300px;
}
#filter-authors {
  width: 150px;
}
#filter-articles {
  width: 150px;
}
#filter-years {
  width: 150px;
}
</style>
